/**
 * 코메 디자인 시스템 — 카카오톡 차용 (SPEC §6).
 * 클라이언트(메신저)와 관리자 웹이 공유한다.
 * CSS 변수로 주입해서 사용: injectCssVariables() 또는 cssVariablesString().
 */

export const colors = {
  // 브랜드
  kakaoYellow: '#FEE500',
  kakaoYellowPressed: '#F2D900',
  kakaoYellowDark: '#FADA0A',
  kakaoBrown: '#3C1E1E',

  // 채팅
  chatBackground: '#B2C7D9', // 클래식 블루 배경
  bubbleMe: '#FEE500',
  bubbleMeText: '#1A1A1A',
  bubbleOther: '#FFFFFF',
  bubbleOtherText: '#1A1A1A',
  unreadYellow: '#F5C342', // 안읽음 카운트(노랑 숫자)

  // 표면
  appBg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceAlt: '#F7F7F7',
  topBar: '#FFFFFF',

  // 텍스트
  textPrimary: '#1A1A1A',
  textSecondary: '#8E8E8E',
  textTertiary: '#B5B5B5',
  textOnYellow: '#3C1E1E',

  // 구분/보더
  divider: '#EDEDED',
  border: '#E5E5E5',

  // 탭
  tabActive: '#1A1A1A',
  tabInactive: '#B5B5B5',
  tabBarBg: '#FFFFFF',

  // 상태
  online: '#33C75A',
  badgeRed: '#FF3B30',
  link: '#3478F6',

  // 관리자 웹 (약간 더 중성적인 톤)
  adminPrimary: '#3C1E1E',
  adminAccent: '#FEE500',
  adminBg: '#F4F5F7',
  adminCard: '#FFFFFF',
  adminSidebar: '#2B2B2B',
  adminSidebarText: '#E8E8E8',
} as const;

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
} as const;

export const radius = {
  sm: '6px',
  md: '10px',
  lg: '16px',
  bubble: '14px',
  avatar: '38%', // 카톡 둥근 사각 프로필
  full: '9999px',
} as const;

export const typography = {
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', 'Malgun Gothic', 'Noto Sans KR', 'Segoe UI', Roboto, sans-serif",
  size: {
    xs: '11px',
    sm: '13px',
    md: '15px',
    lg: '17px',
    xl: '20px',
    xxl: '24px',
  },
  weight: {
    regular: 400,
    medium: 500,
    bold: 700,
  },
} as const;

export const layout = {
  topBarHeight: '52px',
  tabBarHeight: '56px',
  inputBarMinHeight: '52px',
  avatarSize: '48px',
  avatarSizeSm: '36px',
  maxContentWidth: '480px', // 데스크톱에서 모바일 폭 제한
} as const;

export const shadow = {
  bar: '0 1px 0 rgba(0,0,0,0.06)',
  card: '0 1px 3px rgba(0,0,0,0.08)',
  modal: '0 8px 32px rgba(0,0,0,0.18)',
  fab: '0 2px 8px rgba(0,0,0,0.2)',
} as const;

export const tokens = { colors, spacing, radius, typography, layout, shadow } as const;

/** 토큰을 CSS 커스텀 프로퍼티 문자열로 변환 (:root 주입용) */
export function cssVariablesString(): string {
  const lines: string[] = [];
  for (const [k, v] of Object.entries(colors)) lines.push(`  --color-${kebab(k)}: ${v};`);
  for (const [k, v] of Object.entries(spacing)) lines.push(`  --space-${k}: ${v};`);
  for (const [k, v] of Object.entries(radius)) lines.push(`  --radius-${kebab(k)}: ${v};`);
  for (const [k, v] of Object.entries(typography.size)) lines.push(`  --font-size-${k}: ${v};`);
  for (const [k, v] of Object.entries(layout)) lines.push(`  --layout-${kebab(k)}: ${v};`);
  lines.push(`  --font-family: ${typography.fontFamily};`);
  return `:root {\n${lines.join('\n')}\n}`;
}

/** 브라우저에서 :root 에 CSS 변수 주입 */
export function injectCssVariables(): void {
  if (typeof document === 'undefined') return;
  const id = 'kome-design-tokens';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = cssVariablesString();
  document.head.appendChild(style);
}

function kebab(s: string): string {
  return s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}
